import { useState, useEffect, useCallback, useRef } from 'react';
import {
    APIProvider,
    Map,
    InfoWindow,
    useMap,
} from '@vis.gl/react-google-maps';
import LocationMarker from './LocationMarker';

const locations = [
    {
        title: 'Gilbert',
        name: 'Redemption Psychiatry',
        fullQuery: 'Redemption Psychiatry, 2730 S Val Vista Dr Suite 146 Bldg 8, Gilbert, AZ 85295',
        position: { lat: 33.3128, lng: -111.7550 },
        address: '2730 S Val Vista Dr Suite 146 Bldg 8, Gilbert, AZ 85295, USA',
    },
    {
        title: 'Phoenix',
        name: 'Redemption Psychiatry, Phoenix',
        fullQuery: 'Redemption Psychiatry, 12424 N 32nd St Suite 100, Phoenix, AZ 85032',
        position: { lat: 33.5806, lng: -112.0286 },
        address: '12424 N 32nd St Suite 100, Phoenix, AZ 85032, USA',
    },
    {
        title: 'Chandler',
        name: 'Redemption Psychiatry, Chandler',
        fullQuery: 'Redemption Psychiatry, 600 S Dobson Rd Suites E-36 and E-38, Chandler, AZ 85224',
        position: { lat: 33.2961, lng: -111.8906 },
        address: '600 S Dobson Rd Suites E-36 and E-38, Chandler, AZ 85224, USA',
    },
    {
        title: 'Mesa',
        name: 'Redemption Psychiatry Mesa',
        fullQuery: 'Redemption Psychiatry, 3489 E Baseline Rd, Gilbert, AZ 85234',
        position: { lat: 33.3782, lng: -111.7405 },
        address: '3489 E Baseline Rd, Gilbert, AZ 85234, USA',
    },
    {
        title: 'Revolution',
        name: 'Revolution Treatments',
        fullQuery: 'Revolution Treatments, 4920 S Alma School Rd Suite 3, Chandler, AZ 85248',
        position: { lat: 33.2387, lng: -111.8741 },
        address: '4920 S Alma School Rd Suite 3, Chandler, AZ 85248, USA',
    },
];

function MapWithMarkers({ selectedTitle, setSelectedTitle }) {
    const map = useMap();
    const markerRefMap = useRef({});

    useEffect(() => {
        if (selectedTitle && map) {
            const location = locations.find(loc => loc.title === selectedTitle);
            if (location) {
                const offsetCenter = {
                    lat: location.position.lat + 0.01,
                    lng: location.position.lng,
                };
                map.panTo(offsetCenter);
                map.setZoom(14);
            }
        }
    }, [selectedTitle, map]);

    const handleMarkerReady = useCallback((title, markerInstance) => {
        markerRefMap.current[title] = markerInstance;
    }, []);

    return (
        <>
            {locations.map((loc) => (
                <LocationMarker
                    key={loc.title}
                    location={loc}
                    isSelected={selectedTitle === loc.title}
                    onClick={() => setSelectedTitle(loc.title)}
                    onMarkerReady={handleMarkerReady}
                />
            ))}

            {selectedTitle && markerRefMap.current[selectedTitle] && (
                <InfoWindow
                    key={selectedTitle}
                    anchor={markerRefMap.current[selectedTitle]}
                    onCloseClick={() => setSelectedTitle(null)}
                >
                    <div style={{
                        padding: '12px 16px',
                        maxWidth: '240px',
                        backgroundColor: '#ffffff',
                        borderRadius: '12px',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.15)',
                        fontFamily: 'system-ui, sans-serif',
                        lineHeight: '1.4',
                    }}>
                        <h6 style={{
                            margin: '0 0 8px',
                            fontSize: '16px',
                            color: '#0d6efd',
                            fontWeight: '600',
                        }}>
                            {selectedTitle}
                        </h6>
                        <p style={{
                            margin: 0,
                            fontSize: '14px',
                            color: '#333',
                        }}>
                            {locations.find(loc => loc.title === selectedTitle)?.address}
                        </p>
                        <a
                            href={
                                /iPhone|iPad|iPod/.test(navigator.userAgent)
                                    ? `maps://?q=${encodeURIComponent(locations.find(loc => loc.title === selectedTitle)?.fullQuery || '')}`
                                    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(locations.find(loc => loc.title === selectedTitle)?.fullQuery || '')}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                display: 'inline-block',
                                marginTop: '10px',
                                fontSize: '13px',
                                color: '#0d6efd',
                                textDecoration: 'none',
                                fontWeight: '500',
                            }}
                        >
                            View on Maps →
                        </a>
                    </div>
                </InfoWindow>
            )}
        </>
    );
}

export default function LocationMap() {
    const [selectedTitle, setSelectedTitle] = useState(null);

    return (
        <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
            <div className="row">
                <div className="col-md-4 mb-3">
                    <div className="list-group">
                        {locations.map((loc) => (
                            <button
                                key={loc.title}
                                className={`list-group-item list-group-item-action ${selectedTitle === loc.title ? 'active' : ''}`}
                                onClick={() => setSelectedTitle(loc.title)}
                            >
                                <strong>{loc.title}</strong><br />
                                <small>{loc.address}</small>
                            </button>
                        ))}
                    </div>
                </div>

                <div className="col-md-8">
                    <div style={{ width: '100%', height: '400px', borderRadius: '12px', overflow: 'hidden' }}>
                        <Map
                            defaultCenter={{ lat: 33.45, lng: -111.83 }}
                            defaultZoom={10}
                            mapId={process.env.REACT_APP_GOOGLE_MAP_ID}
                        >
                            <MapWithMarkers
                                selectedTitle={selectedTitle}
                                setSelectedTitle={setSelectedTitle}
                            />
                        </Map>
                    </div>
                </div>
            </div>
        </APIProvider>
    );
}
